import React, { createContext, useEffect, useState } from "react";
import { deepCopy } from "../components/leftSide/Models/modelLogic";
import { safeSessionStorageSet } from "../utils/helpers";

export const GraphContext = createContext();

const GraphContextProvider = (props) => {
  // Initialising graphs with an empty graph, so that there is a blank graph on the start of the app
  const [graphs, setGraphs] = useState([]);
  const [graphId, setGraphId] = useState(1);
  const [updatedModelFE_IDs, setUpdatedModelFE_IDs] = useState([]);
  const [requestedFitModel, setRequestedFitModel] = useState([]);
  const [chi2Terms, setChi2Terms] = useState([]);
  const [chi2TermID, setChi2TermID] = useState(0);
  const [selectedChi2Terms, setSelectedChi2Terms] = useState([]);
  const [isGraphsReady, setIsGraphsReady] = useState(false);

  //values not stored in session
  const [fitModalIsOpen, setFitModalIsOpen] = useState(false);

  const [zIndices, setZIndices] = useState(
    deepCopy(graphs).reduce((acc, win) => {
      acc[win.id] = 0;
      return acc;
    }, {})
  );

  function getGraphVariables() {
    return {
      graphs,
      graphId,
      // updatedModelFE_IDs,
      chi2Terms,
      chi2TermID,
      selectedChi2Terms,
    };
  }

  function setGraphVariables(variables) {
    const {
      graphs,
      graphId,
      // updatedModelFE_IDs,
      chi2Terms,
      chi2TermID,
      selectedChi2Terms,
    } = variables;

    setGraphs(graphs);
    setGraphId(graphId);
    // setUpdatedModelFE_IDs(updatedModelFE_IDs);
    setChi2Terms(chi2Terms);
    setChi2TermID(chi2TermID);
    setSelectedChi2Terms(selectedChi2Terms);
  }

  const resetGraphContext = () => {
    setGraphs([]);
    setGraphId(1);
    setUpdatedModelFE_IDs([]);
    setRequestedFitModel([]);
    setChi2Terms([]);
    setChi2TermID(0);
    setSelectedChi2Terms([]);
    // setIsGraphsReady(false);
    setFitModalIsOpen(false);
    setZIndices({});
  };

  // Function to sync UI state with what's actually stored in sessionStorage
  const syncWithSessionStorage = () => {
    try {
      // Helper function to safely parse JSON from sessionStorage
      const safeJsonParse = (key) => {
        try {
          const item = window.sessionStorage.getItem(key);
          if (
            item === null ||
            item === undefined ||
            item === "undefined" ||
            item === ""
          )
            return null;
          return JSON.parse(item);
        } catch (error) {
          console.warn(`Error parsing ${key} from sessionStorage:`, error);
          return null;
        }
      };

      // Read all values from sessionStorage and update state
      const graphIdSession = safeJsonParse("graphId");
      const graphsSession = safeJsonParse("graphs");
      const chi2TermsSession = safeJsonParse("chi2Terms");
      const chi2TermIDSession = safeJsonParse("chi2TermID");
      const selectedChi2TermsSession = safeJsonParse("selectedChi2Terms");

      // Update state with what's actually stored - this will remove unsaved data
      setGraphs(graphsSession || []);
      if (graphIdSession !== null) setGraphId(graphIdSession);
      setChi2Terms(chi2TermsSession || []);
      if (chi2TermIDSession !== null) setChi2TermID(chi2TermIDSession);
      setSelectedChi2Terms(selectedChi2TermsSession || []);

    } catch (error) {
      console.error("Error syncing graph state with sessionStorage:", error);
    }
  };

  // Function to immediately clean up unsaved data when a save fails
  const cleanupUnsavedData = () => {
    syncWithSessionStorage();
  };

  useEffect(() => {
    // Helper function to safely parse JSON from sessionStorage
    const safeJsonParse = (key) => {
      try {
        const item = window.sessionStorage.getItem(key);
        if (
          item === null ||
          item === undefined ||
          item === "undefined" ||
          item === ""
        )
          return null;
        return JSON.parse(item);
      } catch (error) {
        console.warn(`Error parsing ${key} from sessionStorage:`, error);
        return null;
      }
    };

    // Use the safe parsing function for all items
    const graphIdSession = safeJsonParse("graphId");
    const graphsSession = safeJsonParse("graphs");
    const chi2TermsSession = safeJsonParse("chi2Terms");
    const chi2TermIDSession = safeJsonParse("chi2TermID");
    const selectedChi2TermsSession = safeJsonParse("selectedChi2Terms");

    if (graphIdSession != null) {
      setGraphId(graphIdSession);
    }

    if (graphsSession != null) {
      setGraphs(graphsSession);
    }

    if (chi2TermsSession != null) {
      setChi2Terms(chi2TermsSession);
    }

    if (chi2TermIDSession != null) {
      setChi2TermID(chi2TermIDSession);
    }

    if (selectedChi2TermsSession != null) {
      setSelectedChi2Terms(selectedChi2TermsSession);
    }

    setIsGraphsReady(true);
  }, []);

  useEffect(() => {
    // Track which variables failed to save
    const saveResults = {
      graphs: safeSessionStorageSet("graphs", JSON.stringify(graphs), cleanupUnsavedData),
      graphId: safeSessionStorageSet("graphId", JSON.stringify(graphId)),
      chi2Terms: safeSessionStorageSet("chi2Terms", JSON.stringify(chi2Terms)),
      chi2TermID: safeSessionStorageSet("chi2TermID", JSON.stringify(chi2TermID)),
      selectedChi2Terms: safeSessionStorageSet("selectedChi2Terms", JSON.stringify(selectedChi2Terms)),
    };

    // Log any failed saves for debugging
    const failedSaves = Object.entries(saveResults).filter(([, success]) => !success);
    if (failedSaves.length > 0) {
      console.warn("Failed to save graph variables to sessionStorage:", failedSaves.map(([key]) => key));
    }
  }, [graphs, graphId, chi2Terms, chi2TermID, selectedChi2Terms]);

  const value = {
    resetGraphContext,
    graphs,
    setGraphs,
    graphId,
    setGraphId,
    updatedModelFE_IDs,
    setUpdatedModelFE_IDs,
    requestedFitModel,
    setRequestedFitModel,
    chi2Terms,
    setChi2Terms,
    fitModalIsOpen,
    setFitModalIsOpen,
    chi2TermID,
    setChi2TermID,
    selectedChi2Terms,
    setSelectedChi2Terms,
    isGraphsReady,
    zIndices,
    setZIndices,
    getGraphVariables,
    setGraphVariables,
    syncWithSessionStorage,
    cleanupUnsavedData,
  };

  return (
    <GraphContext.Provider value={value}>
      {props.children}
    </GraphContext.Provider>
  );
};

export default GraphContextProvider;
